import { useState, useCallback } from 'react';
import { Company, ScrapeJob, SystemHealth } from '../types';
import { apiService } from '../services/api';

export const useAdmin = (adminKey: string = 'dev-secret-admin-key') => {
  const [isSeeding, setIsSeeding] = useState<boolean>(false);
  const [isScraping, setIsScraping] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const seedDefaultCatalog = useCallback(async () => {
    setIsSeeding(true);
    setError(null);
    try {
      await apiService.seedCatalog(adminKey);
      return true;
    } catch (err: any) {
      setError(err.message || 'Failed to seed catalog');
      return false;
    } finally {
      setIsSeeding(false);
    }
  }, [adminKey]);

  const addCompany = useCallback(
    async (companyData: Partial<Company> & { policies?: Record<string, { name: string; url: string }>; triggerImmediateScrape?: boolean }) => {
      setError(null);
      try {
        const result = await apiService.addCompany(companyData, adminKey);
        if (companyData.triggerImmediateScrape && result.id) {
          await apiService.triggerScrape(result.id, 'terms_of_service', adminKey);
        }
        return result;
      } catch (err: any) {
        setError(err.message || 'Failed to add company');
        throw err;
      }
    },
    [adminKey]
  );

  const deleteCompany = useCallback(
    async (companyId: string) => {
      setError(null);
      try {
        await apiService.deleteCompany(companyId, adminKey);
        return true;
      } catch (err: any) {
        setError(err.message || 'Failed to delete company');
        throw err;
      }
    },
    [adminKey]
  );

  const triggerManualScrape = useCallback(
    async (companyId: string, policyType: string = 'terms_of_service'): Promise<ScrapeJob> => {
      setIsScraping(true);
      setError(null);
      try {
        return await apiService.triggerScrape(companyId, policyType, adminKey);
      } catch (err: any) {
        setError(err.message || 'Manual scrape trigger failed');
        throw err;
      } finally {
        setIsScraping(false);
      }
    },
    [adminKey]
  );

  const checkHealth = useCallback(async (): Promise<SystemHealth> => {
    return apiService.checkHealth();
  }, []);

  return {
    seedDefaultCatalog,
    addCompany,
    deleteCompany,
    triggerManualScrape,
    checkHealth,
    isSeeding,
    isScraping,
    error,
  };
};
